import type { FC, ReactNode } from 'react'
import type { OfferCardData } from '@/types'

import Link from 'next/link'
import { ArrowLeft, ArrowRight, BadgePercent, CalendarClock, CircleCheck } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useI18n } from '@/lib/i18n'
import { CmsText } from '@/components/store/cms-text'
import { Countdown } from '@/components/store/countdown'
import { Num, Price } from '@/components/store/price'

export const OfferHeadline: FC<{ offer: Pick<OfferCardData, 'discountType' | 'discountValue'>, className?: string }> = ({ offer, className }): ReactNode => {
    const { tx } = useI18n()

    if (offer.discountType === 'percent') {
        return (
            <span className={cn('inline-flex items-baseline gap-2', className)}>
                {tx('خصم', 'Save')}
                <span dir='ltr' className='tabular-nums'><span>{offer.discountValue}</span>%</span>
            </span>
        )
    }

    if (offer.discountType === 'fixed_price') {
        return (
            <span className={cn('inline-flex flex-wrap items-baseline gap-2', className)}>
                {tx('بسعر خاص', 'Special price')}
                <Price value={offer.discountValue} />
            </span>
        )
    }

    return (
        <span className={cn('inline-flex flex-wrap items-baseline gap-2', className)}>
            {tx('وفّر', 'Save')}
            <Price value={offer.discountValue} />
        </span>
    )
}

export const offerHref = (offer: OfferCardData): string => {
    if (offer.appliesTo === 'categories' && offer.categories.length === 1) return `/categories/${offer.categories[0].slug}`
    if (offer.appliesTo === 'products' && offer.products.length === 1) return `/products/${offer.products[0].slug}`
    if (offer.appliesTo === 'all') return '/products'

    return `/offers#offer-${offer.id}`
}

interface OfferCardProps {
    offer: OfferCardData
    className?: string
    showLink?: boolean
}

export const OfferCard: FC<OfferCardProps> = ({ offer, className, showLink = true }): ReactNode => {
    const { tx, txn, isRtl } = useI18n()
    const Arrow = isRtl ? ArrowLeft : ArrowRight

    const conditions: Array<ReactNode> = []

    if (offer.priceTier === 'retail') conditions.push(tx('يُطبّق على سعر القطاعي', 'Applies to the retail price'))
    else if (offer.priceTier === 'wholesale') conditions.push(tx('يُطبّق على سعر الجملة', 'Applies to the wholesale price'))
    else conditions.push(tx('يُطبّق على سعري الجملة والقطاعي', 'Applies to retail and wholesale prices'))

    if (offer.minQty > 1) {
        conditions.push(txn('عند شراء {n} أو أكثر من نفس المنتج', 'When you buy {n} or more of the same product', { n: <Num value={offer.minQty} className='font-semibold text-white' /> }))
    }

    if (offer.appliesTo === 'all') conditions.push(tx('على كل منتجات المتجر', 'On every product in the store'))

    return (
        <article id={`offer-${offer.id}`} className={cn('relative flex h-full scroll-mt-28 flex-col overflow-hidden rounded-3xl bg-navy-950 p-6 text-white sm:p-7', className)}>
            <div className='absolute -end-16 -top-20 size-64 rounded-full bg-cyan-500/25 blur-3xl' aria-hidden='true' />
            <div className='absolute -bottom-24 -start-10 size-56 rounded-full bg-navy-600/40 blur-3xl' aria-hidden='true' />

            {offer.image && (
                <img src={offer.image} alt='' loading='lazy' decoding='async' className='absolute inset-0 h-full w-full object-cover opacity-20' aria-hidden='true' />
            )}

            <div className='relative flex flex-1 flex-col gap-5'>
                <div className='flex items-center justify-between gap-3'>
                    <span className='inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold text-cyan-200 ring-1 ring-white/15'>
                        {offer.upcoming ? <CalendarClock className='size-3.5' aria-hidden='true' /> : <BadgePercent className='size-3.5' aria-hidden='true' />}
                        {offer.upcoming ? tx('يبدأ قريباً', 'Coming soon') : tx('عرض ساري', 'Live offer')}
                    </span>
                </div>

                <div className='flex flex-col gap-2'>
                    <OfferHeadline offer={offer} className='text-3xl font-bold text-cyan-300 sm:text-4xl' />
                    <CmsText table='offers' id={offer.id} field={offer.title} as='h3' className='text-xl font-semibold leading-snug' />
                    {offer.description && (
                        <CmsText table='offers' id={offer.id} field={offer.description} as='p' className='text-sm leading-relaxed text-white/70' />
                    )}
                </div>

                <ul className='flex flex-col gap-2 text-sm text-white/80'>
                    {conditions.map((condition, index) => (
                        <li key={index} className='flex items-start gap-2'>
                            <CircleCheck className='mt-0.5 size-4 shrink-0 text-cyan-300' aria-hidden='true' />
                            <span>{condition}</span>
                        </li>
                    ))}
                </ul>

                {offer.categories.length > 0 && (
                    <div className='flex flex-wrap gap-2'>
                        {offer.categories.map(category => (
                            <Link key={category.id} href={`/categories/${category.slug}`} className='rounded-full bg-white/10 px-3 py-1.5 text-xs font-medium text-white ring-1 ring-white/15 transition-colors hover:bg-white/20'>
                                <CmsText table='categories' id={category.id} field={category.name} />
                            </Link>
                        ))}
                    </div>
                )}

                <div className='mt-auto flex flex-wrap items-end justify-between gap-4 pt-2'>
                    {offer.upcoming && offer.startsAt
                        ? <Countdown target={offer.startsAt} mode='starts' tone='light' />
                        : offer.endsAt
                            ? <Countdown target={offer.endsAt} tone='light' />
                            : <span className='text-xs text-white/60'>{tx('ساري حتى إشعار آخر', 'Running until further notice')}</span>}

                    {showLink && !offer.upcoming && (
                        <Link href={offerHref(offer)} className='group inline-flex h-11 items-center gap-2 rounded-full bg-white px-5 text-sm font-semibold text-navy-950 transition-all hover:bg-cyan-50 active:scale-[0.98]'>
                            {tx('تسوق العرض', 'Shop the offer')}
                            <Arrow className='size-4 transition-transform group-hover:-translate-x-0.5 ltr:group-hover:translate-x-0.5' aria-hidden='true' />
                        </Link>
                    )}
                </div>
            </div>
        </article>
    )
}
